/**
 * FluidProperties
 * Provides access to glycol fluid property data (density, specific heat)
 * with interpolation for values between table points
 */

import { 
  ethyleneGlycolDensity, 
  propylenGlycolDensity, 
  ethyleneGlycolSpecificHeat, 
  propylenGlycolSpecificHeat 
} from './FluidPropertiesData.js'

/**
 * Get fluid property data object
 * @param {string} fluidType - 'ethylene' or 'propylene'
 * @param {string} property - 'density' or 'specificHeat'
 * @returns {Object} Fluid property data object
 */
function getFluidData(fluidType, property) {
  if (fluidType === 'ethylene') {
    return property === 'density' ? ethyleneGlycolDensity : ethyleneGlycolSpecificHeat
  } else if (fluidType === 'propylene') {
    return property === 'density' ? propylenGlycolDensity : propylenGlycolSpecificHeat
  }
  return null
}

/**
 * Linear interpolation helper
 * @param {number} x1 - First x coordinate
 * @param {number} y1 - First y coordinate
 * @param {number} x2 - Second x coordinate
 * @param {number} y2 - Second y coordinate
 * @param {number} x - Target x value
 * @returns {number} Interpolated y value
 */
function linearInterpolate(x1, y1, x2, y2, x) {
  if (y1 === null || y1 === undefined || y2 === null || y2 === undefined) {
    return null
  }
  return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1)
}

/**
 * Get fluid property value at given temperature and concentration
 * Uses linear interpolation if exact temperature not in table
 * 
 * @param {string} fluidType - 'ethylene' or 'propylene'
 * @param {string} property - 'density' or 'specificHeat'
 * @param {number} temperature - Temperature in °F
 * @param {number} concentration - Concentration in % (10, 20, ..., 90)
 * @returns {number|null} Property value or null if data not available
 */
function getPropertyValue(fluidType, property, temperature, concentration) {
  const fluidData = getFluidData(fluidType, property)
  if (!fluidData) return null

  const { temperatures, data } = fluidData

  // Validate inputs
  if (!temperature && temperature !== 0) return null
  if (!concentration && concentration !== 0) return null

  // Clamp temperature to available range
  const minTemp = Math.min(...temperatures)
  const maxTemp = Math.max(...temperatures)
  
  if (temperature < minTemp || temperature > maxTemp) {
    // Use edge values without interpolation for out-of-range temps
    const clampedTemp = temperature < minTemp ? minTemp : maxTemp
    const tempStr = String(clampedTemp)
    const concStr = String(concentration)
    return data[tempStr]?.[concStr] || null
  }

  // Check if we have exact temperature in table
  const tempStr = String(temperature)
  const concStr = String(concentration)
  if (data[tempStr] && data[tempStr][concStr] !== undefined) {
    return data[tempStr][concStr]
  }

  // Find surrounding temperatures for interpolation
  let lowerTemp = null
  let upperTemp = null

  for (const temp of temperatures) {
    if (temp < temperature) {
      lowerTemp = temp
    } else if (temp > temperature && upperTemp === null) {
      upperTemp = temp
      break
    }
  }

  // If no surrounding temperatures, use closest
  if (lowerTemp === null || upperTemp === null) {
    const closest = temperatures.reduce((prev, curr) => 
      Math.abs(curr - temperature) < Math.abs(prev - temperature) ? curr : prev
    )
    const closestStr = String(closest)
    return data[closestStr]?.[concStr] || null
  }

  // Interpolate between lower and upper temperatures
  const lowerTempStr = String(lowerTemp)
  const upperTempStr = String(upperTemp)
  const y1 = data[lowerTempStr]?.[concStr]
  const y2 = data[upperTempStr]?.[concStr]

  if (y1 === null || y1 === undefined || y2 === null || y2 === undefined) {
    return null
  }

  return linearInterpolate(lowerTemp, y1, upperTemp, y2, temperature)
}

/**
 * Get glycol correction factor for fluid properties
 * Returns ratio of water density to fluid density
 * 
 * @param {string} fluidType - 'water', 'propylene', or 'ethylene'
 * @param {number} temperature - Average fluid temperature in °F
 * @param {number} concentration - Glycol concentration in % (10-90)
 * @returns {number} Density correction factor (1.0 for water)
 */
function getGlycolDensityCorrectionFactor(fluidType, temperature, concentration) {
  if (fluidType === 'water') {
    return 1.0 // Water has no correction
  }

  // Standard water density at reference temperature (60°F): ~62.3 lbm/ft³
  const waterDensity = 62.3

  // Get fluid density at target temperature and concentration
  const fluidDensity = getPropertyValue(fluidType, 'density', temperature, concentration)

  if (fluidDensity === null) {
    console.warn(`Could not find density data for ${fluidType} at ${temperature}°F, ${concentration}%`)
    return 1.0 // Return no correction if data unavailable
  }

  // Return ratio: water density / fluid density
  return waterDensity / fluidDensity
}

/**
 * Get specific heat correction factor for glycol fluid
 * Returns ratio of fluid Cp to water Cp
 * 
 * @param {string} fluidType - 'water', 'propylene', or 'ethylene'
 * @param {number} temperature - Average fluid temperature in °F
 * @param {number} concentration - Glycol concentration in % (10-90)
 * @returns {number} Specific heat correction factor (1.0 for water)
 */
function getGlycolSpecificHeatCorrectionFactor(fluidType, temperature, concentration) {
  if (fluidType === 'water') {
    return 1.0 // Water has no correction
  }

  // Standard water specific heat at 60°F: ~1.0 Btu/lbm·°F
  const waterCp = 1.0

  // Get fluid specific heat at target temperature and concentration
  const fluidCp = getPropertyValue(fluidType, 'specificHeat', temperature, concentration)

  if (fluidCp === null) {
    console.warn(`Could not find specific heat data for ${fluidType} at ${temperature}°F, ${concentration}%`)
    return 1.0 // Return no correction if data unavailable
  }

  // Return ratio: fluid Cp / water Cp
  return fluidCp / waterCp
}

/**
 * Get combined glycol correction factor
 * Accounts for both density and specific heat effects
 * 
 * @param {string} fluidType - 'water', 'propylene', or 'ethylene'
 * @param {number} temperature - Average fluid temperature in °F
 * @param {number} concentration - Glycol concentration in % (10-90)
 * @returns {number} Combined correction factor
 */
function getGlycolCorrectionFactor(fluidType, temperature, concentration) {
  if (fluidType === 'water') {
    return 1.0
  }

  const densityFactor = getGlycolDensityCorrectionFactor(fluidType, temperature, concentration)
  const cpFactor = getGlycolSpecificHeatCorrectionFactor(fluidType, temperature, concentration)

  // Combined factor: densityFactor / cpFactor
  // This accounts for both density and specific heat changes
  return densityFactor / cpFactor
}

export { getGlycolCorrectionFactor, getGlycolDensityCorrectionFactor, getGlycolSpecificHeatCorrectionFactor }
